import React, { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import BasicDialog from 'scenes/Dialog/components/BasicDialog';
import FormDialog from 'scenes/Dialog/components/FormDialog';
import SendMessageForm from 'scenes/Form/components/SendMessageForm';
import ResponseErrorDialog from 'scenes/Dialog/components/ResponseErrorDialog';
import WriteReviewDialog from 'scenes/Dialog/components/WriteReviewDialog';

import { Role, showNoCandidateProfileDialog } from 'helpers/user';
import { useApi } from 'hooks/useApi';
import { ENDPOINT } from 'constants/endpoints';
import { MESSAGE_TYPE } from 'constants/message-types';
import { POPUP_STYLES } from 'constants/popup-styles';

export const useReviewAction = (user, options) => {
  const history = useHistory();
  const api = useApi();

  const [isAnonymous, isCandidate, isRecruiter] = useMemo(
    () => [
      Role.isAnonymous(user),
      Role.isCandidate(user),
      Role.isRecruiter(user),
    ],
    [user]
  );

  const isAllowed = useCallback(
    ({ AllowView, ReviewType }, action) => {
      if (
        (isAnonymous && 'view' === action) ||
        (isRecruiter && isCandidate) ||
        (isCandidate && AllowView === 'Yes')
      ) {
        return true;
      }

      if (!isCandidate) {
        showNoCandidateProfileDialog(user);
      } else {
        WriteReviewDialog().then(url => history.push(url));
      }

      return false;
    },
    [isAnonymous, isCandidate, isRecruiter]
  );

  const isAllowedView = useCallback(review => isAllowed(review, 'view'), [
    isAllowed,
  ]);
  const isAllowedRate = useCallback(review => isAllowed(review, 'rate'), [
    isAllowed,
  ]);
  const isAllowedSendMessage = useCallback(
    review => isAllowed(review, 'message'),
    [isAllowed]
  );

  const view = useCallback(
    (event, review) => {
      if (!isAllowedView(review)) {
        event.preventDefault();
      }
    },
    [isAllowedView]
  );

  const rate = useCallback(
    (review, value) =>
      new Promise((resolve, reject) => {
        const { ReviewType, ReviewID } = review;
        if (isAllowedRate(review)) {
          api.post(
            ENDPOINT.ACTION.REVIEW.POST_RATING,
            {
              id: ReviewID,
              type: ReviewType,
              mark: value,
            },
            {
              handleError: response => {
                ResponseErrorDialog({ response });
                reject();
              },
              handleSuccess: response => {
                BasicDialog({ bodyRender: 'Thank you for your response' });
                resolve(response);
              },
            }
          );
        } else {
          reject();
        }
      }),
    [isAllowedRate]
  );

  const message = useCallback(
    review => {
      if (isAllowedSendMessage(review)) {
        FormDialog({
          title: 'Message to Reviewer',
          modeless: false,
          compact: true,
          popupStyles: { padding: '0', maxWidth: '800px' },
          popupInnerStyles: { padding: '0 30px' },
          params: {
            recipients: review.CandidateID,
            messageType: MESSAGE_TYPE.TYPE_REVIEW_APPEAL,
            review,
            upgradeRequestEmail: options.upgradeRequestEmail,
          },
          form: (actions, params) => (
            <SendMessageForm actions={actions} params={params} />
          ),
        }).then(
          message =>
            BasicDialog({
              bodyRender: message,
              bodyStyles: POPUP_STYLES.warningBodyStyles,
              popupStyles: POPUP_STYLES.warningPopupStyles,
              popupInnerStyles: POPUP_STYLES.warningContentStyles,
              withoutButtons: true,
            }),
          response => ResponseErrorDialog({ response })
        );
      }
    },
    [isAllowedSendMessage, options]
  );

  return {
    isAllowedView,
    isAllowedRate,
    isAllowedSendMessage,
    view,
    rate,
    message,
  };
};
